import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Phone, MapPin, Wallet, AlertTriangle, ClipboardList, Ban } from 'lucide-react';

import {
  getCustomer,
  setOrderStatus,
  addOrderPayment,
  type SupplierOrderAction,
} from '@/services/orders';
import { Spinner, EmptyState } from '@/components/fields';
import { rub, dateTime } from '@/lib/format';
import { useAuthStore } from '@/store/auth.store';
import { OrderModal, StatusBadges } from '@/components/OrderModal';

export default function CustomerDetail() {
  const { establishmentId = '' } = useParams();
  const qc = useQueryClient();
  const supplier = useAuthStore((s) => s.supplier);
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customer', establishmentId],
    queryFn: () => getCustomer(establishmentId),
    enabled: !!establishmentId,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['customer', establishmentId] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    qc.invalidateQueries({ queryKey: ['debts'] });
  };

  const status = useMutation({
    mutationFn: ({ id, action }: { id: string; action: SupplierOrderAction }) =>
      setOrderStatus(id, action),
    onSuccess: (_d, v) => {
      invalidate();
      toast.success(
        v.action === 'confirm' ? 'Заказ подтверждён' : v.action === 'reject' ? 'Заказ отклонён' : 'Заказ отгружен',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const pay = useMutation({
    mutationFn: ({ id, amount, note }: { id: string; amount: number; note?: string }) =>
      addOrderPayment(id, { amount, note }),
    onSuccess: () => {
      invalidate();
      toast.success('Оплата отмечена');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const est = data?.establishment;
  const orders = data?.orders ?? [];
  const selected = openId ? orders.find((o) => o.id === openId) ?? null : null;

  return (
    <div>
      <Link
        to="/debts"
        className="inline-flex items-center gap-1.5 text-sm text-ink/55 hover:text-ink mb-3"
      >
        <ArrowLeft size={16} /> К долгам
      </Link>

      {isLoading ? (
        <Spinner label="Загружаем заведение…" />
      ) : !est ? (
        <EmptyState
          icon={<ClipboardList size={40} strokeWidth={1.4} />}
          title="Заведение не найдено"
          description="Возможно, у него ещё нет заказов."
        />
      ) : (
        <>
          {/* Шапка заведения */}
          <div className="card p-5 mb-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-ink flex items-center gap-2">
                  {est.name}
                  {est.isBlocked && (
                    <span className="badge bg-red-50 text-red-700 inline-flex items-center gap-1">
                      <Ban size={12} /> Заблокирован
                    </span>
                  )}
                </h1>
                <div className="text-xs text-ink/50 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                  {est.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone size={12} /> {est.phone}
                    </span>
                  )}
                  {est.address && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} /> {est.address}
                    </span>
                  )}
                  <span>{est.ordersCount} заказ(ов) в работе</span>
                </div>
                {est.note && <div className="text-xs text-ink/50 mt-1 italic">«{est.note}»</div>}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              <Kpi label="Текущий долг" value={rub(est.currentDebt)} accent="amber" />
              <Kpi label="Просрочено" value={rub(est.overdueDebt)} accent="red" />
              <Kpi label="Оборот всего" value={rub(est.totalOrdered)} accent="ink" />
            </div>
          </div>

          {/* Все заказы заведения */}
          <div className="flex items-center gap-2 mb-2 text-ink/70">
            <ClipboardList size={16} />
            <h2 className="text-sm font-semibold">Все заказы</h2>
            <span className="text-xs text-ink/40">{orders.length}</span>
          </div>

          {orders.length === 0 ? (
            <EmptyState
              icon={<Wallet size={40} strokeWidth={1.4} />}
              title="Заказов нет"
              description="Здесь появятся все заказы этого заведения."
            />
          ) : (
            <div className="card divide-y divide-line">
              {orders.map((o) => {
                const owed = Math.max(0, o.totalCost - o.paidAmount);
                const inDebt =
                  o.status !== 'NEW' &&
                  o.status !== 'REJECTED' &&
                  o.status !== 'CANCELLED' &&
                  o.paymentTerms !== 'PREPAY' &&
                  o.debtStatus !== 'PAID';
                return (
                  <button
                    key={o.id}
                    onClick={() => setOpenId(o.id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-paper transition"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-ink/45">{dateTime(o.createdAt)} · {o.items.length} поз.</div>
                      <div className="mt-1">
                        <StatusBadges order={o} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-ink">{rub(o.totalCost)}</div>
                      {inDebt && owed > 0 && (
                        <div className="text-[11px] text-amber-700 flex items-center justify-end gap-1 mt-0.5">
                          <AlertTriangle size={11} /> долг {rub(owed)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {selected && (
        <OrderModal
          order={selected}
          supplier={supplier}
          busy={status.isPending || pay.isPending}
          onClose={() => setOpenId(null)}
          onAction={(action) => status.mutate({ id: selected.id, action })}
          onPay={(amount, note) => pay.mutate({ id: selected.id, amount, note })}
        />
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'amber' | 'red' | 'ink';
}) {
  const color =
    accent === 'amber' ? 'text-amber-700' : accent === 'red' ? 'text-red-600' : 'text-ink';
  return (
    <div className="rounded-xl bg-paper border border-line p-3">
      <div className="text-xs text-ink/45">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
